import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Header } from '../components/Header'
import { useAlert } from '../contexts/AlertContext'
import { useAuth } from '../hooks/useAuth'
import { attendanceGetAccess } from '../lib/apiPhpAdapter'
import {
  ATT_HR_SECTIONS,
  AttendanceHrExtrasPanel,
  isAttendanceHrSection,
  type AttendanceHrSection,
} from './attendance/AttendanceHrExtrasPanel'
import './AttendanceView.css'
import './attendance/AttendanceOrgAdmin.css'

function monthTitle(year: number, month: number): string {
  return `${String(month).padStart(2, '0')}/${year}`
}

export default function AttendancePersonalistaManageView() {
  const { section: sectionParam } = useParams<{ section: string }>()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { showAlert } = useAlert()
  const [gateOk, setGateOk] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)

  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const section: AttendanceHrSection | null = isAttendanceHrSection(sectionParam)
    ? sectionParam
    : null

  useEffect(() => {
    if (authLoading || !user) return
    let cancelled = false
    attendanceGetAccess()
      .then((a) => {
        if (cancelled) return
        if (!a.canPersonalista) {
          setGateOk(false)
          navigate('/attendance', { replace: true })
          return
        }
        setGateOk(true)
      })
      .catch(() => {
        if (!cancelled) navigate('/attendance', { replace: true })
      })
    return () => {
      cancelled = true
    }
  }, [authLoading, user, navigate])

  const shiftMonth = (delta: number) => {
    let m = month + delta
    let y = year
    if (m < 1) {
      m = 12
      y -= 1
    } else if (m > 12) {
      m = 1
      y += 1
    }
    setYear(y)
    setMonth(m)
  }

  if (authLoading || !user || gateOk === null) {
    return (
      <div className="app">
        <Header title="Docházka — Personalista" showLogo />
        <div style={{ padding: '2rem', color: '#fff' }}>Načítání…</div>
      </div>
    )
  }

  if (!section) {
    return <Navigate to="/attendance/personalista/zustatky" replace />
  }

  const sectionLabel = ATT_HR_SECTIONS.find(([k]) => k === section)?.[1] ?? section
  const needsPeriod = section === 'sestavy' || section === 'avensio' || section === 'banka'

  return (
    <div className="app app--att-hr">
      <Header
        leftButtons={
          <button type="button" className="back-button" onClick={() => navigate('/attendance')}>
            ← Zpět na výběr role
          </button>
        }
        title="Docházka — Personalista"
        rightContent={
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>{user.name}</span>
        }
        showLogo
      />

      <div className="attendance-root">
        <aside className="attendance-sidebar">
          <div className="attendance-status">
            <div className="attendance-status__row">
              <span className="attendance-status__label">Personalista</span>
            </div>
            <div className="attendance-status__hint">Měsíční přehled a správa docházky.</div>
          </div>

          <nav className="attendance-nav">
            <button
              type="button"
              className="attendance-nav__item"
              onClick={() => navigate('/attendance/personalista')}
            >
              Měsíční přehled
            </button>
            {ATT_HR_SECTIONS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={k === section ? 'attendance-nav__item active' : 'attendance-nav__item'}
                onClick={() => navigate(`/attendance/personalista/${k}`)}
              >
                {label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="attendance-content">
          <div className="attendance-content__head">
            <h2>{sectionLabel}</h2>
            {needsPeriod ? (
              <div className="attendance-content__period">
                <button type="button" disabled={busy} onClick={() => shiftMonth(-1)}>
                  ‹
                </button>
                <strong>{monthTitle(year, month)}</strong>
                <button type="button" disabled={busy} onClick={() => shiftMonth(1)}>
                  ›
                </button>
              </div>
            ) : null}
          </div>

          <AttendanceHrExtrasPanel
            section={section}
            year={year}
            month={month}
            busy={busy}
            setBusy={setBusy}
            showAlert={showAlert}
          />
        </div>
      </div>
    </div>
  )
}
